import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/apiConfig';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import LocationViewDetails from './LocationViewDetails';
import './LocationViewModal.css';

function LocationViewModal({ isOpen, locationId, token, onClose, currentUser }) {
  const [location, setLocation] = useState(null);
  const [tatState, setTatState] = useState({ loading: true, error: false, current: null, next: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow || 'unset';
      document.documentElement.style.overflow = previousHtmlOverflow || 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !locationId) {
      setLocation(null);
      setTatState({ loading: false, error: false, current: null, next: null });
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDetails = async () => {
      setLoading(true);
      setTatState({ loading: true, error: false, current: null, next: null });

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [locationRes, tatRes] = await Promise.all([
          axios.get(API_ENDPOINTS.LOCATIONS.GET(locationId), { headers }),
          axios.get(API_ENDPOINTS.COMPLAINT_TAT.SUMMARY(locationId), { headers }).catch(() => null),
        ]);

        if (cancelled) return;

        setLocation(locationRes.data);

        if (tatRes?.data?.data) {
          setTatState({
            loading: false,
            error: false,
            current: tatRes.data.data.current || null,
            next: tatRes.data.data.next || null,
          });
        } else {
          setTatState({ loading: false, error: true, current: null, next: null });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching location details:', error);
        toast.error('Failed to fetch location details');
        setLocation(null);
        setTatState({ loading: false, error: true, current: null, next: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      cancelled = true;
    };
  }, [isOpen, locationId, token]);

  if (!isOpen || !locationId) return null;

  const content = (
    <div className="location-view-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="location-view-modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Location details (read-only)"
      >
        <div className="location-view-modal-panel-header">
          <h2>Location details (read-only)</h2>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <div className="location-view-modal-panel-body">
          {loading ? (
            <div className="location-view-modal-loading">
              <p>Loading location details…</p>
            </div>
          ) : location ? (
            <LocationViewDetails
              location={location}
              currentUser={currentUser}
              tatState={tatState}
            />
          ) : (
            <div className="location-view-modal-loading">
              <p>Unable to load location details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default LocationViewModal;
